/**
 * Open-from-pin path for `.ipynb` (notebook) FILE-backed workspace pins. Split
 * out of `openFilePin.ts` (K8) — see that module's header for the file-pin
 * rationale (sourcePin-only dedup, explicit envelope binding, on-focus disk
 * revalidation).
 */
import { getFileNameFromPath } from '../../../workspace/query-editor/utils/queryFileUtils';
import { openNotebookFile } from '../../../workspace/notebook/services/notebookFileApi';
import { deserializeFromIpynb } from '../../../workspace/notebook/utils/ipynbDeserializer';
import { computeNotebookHash } from '../../../workspace/notebook/utils/ipynbSerializer';
import { isNotebookTab } from '../../../workspace/core/types/workspaceTypes';
import type { Tab } from '../../../workspace/core/types/workspaceTypes';
import type { PinnedWorkspaceRef } from '../../../../types/electron';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import {
  connectionInfoFor,
  findLinkedTabs,
  mostRecentlyActive,
  type WorkspaceState,
} from './openWorkspacePin';
import { showFileOpenError, warnUnsavedConflict, type FilePinOpenMode } from './openFilePin';

export async function openNotebookFilePin({
  connectionId,
  ref,
  filePath,
}: {
  connectionId: string;
  ref: PinnedWorkspaceRef;
  filePath: string;
}): Promise<FilePinOpenMode> {
  const result = await openNotebookFile({ filePath });
  if (!result.success) {
    showFileOpenError(filePath, result.error);
    return null;
  }

  let content: ReturnType<typeof deserializeFromIpynb>['content'];
  try {
    content = deserializeFromIpynb(result.content).content;
  } catch {
    showFileOpenError(result.filePath ?? filePath);
    return null;
  }
  const freshHash = computeNotebookHash(content);

  // Re-read workspace state AFTER the disk read (K3).
  const ws = useWorkspaceStore.getState();
  const linked = findLinkedTabs(connectionId, ref.id, ws);

  if (linked.length > 0) {
    revalidateNotebookTab(mostRecentlyActive(linked, ws), content, freshHash, result.filePath, ws);
    return 'focus';
  }

  const tabId = ws.addTab({
    type: 'notebook',
    title: getFileNameFromPath(result.filePath),
    content: { ...content, filePath: result.filePath, lastSavedHash: freshHash },
    // Explicit envelope binding (G3) — notebook file-open normally sets none.
    connectionInfo: connectionInfoFor(connectionId, ref.database),
  });
  ws.setSourcePin(tabId, { connectionId, pinId: ref.id });
  return 'new';
}

function revalidateNotebookTab(
  tab: Tab,
  freshContent: ReturnType<typeof deserializeFromIpynb>['content'],
  freshHash: string,
  filePath: string,
  ws: WorkspaceState,
): void {
  ws.setActiveTab(tab.id);
  if (!isNotebookTab(tab)) return;

  // `computeNotebookHash` covers only cellType/source/chartConfig, so a disk
  // change to notebook METADATA (saveMode/connectionName/engine) leaves the
  // hash equal. Deep-compare metadata as well so a metadata-only edit still
  // revalidates (K4) — don't change `computeNotebookHash` (it drives
  // dirty-tracking app-wide).
  const hashUnchanged = tab.content.lastSavedHash === freshHash;
  const metadataUnchanged =
    stableStringify(tab.content.metadata) === stableStringify(freshContent.metadata);
  if (hashUnchanged && metadataUnchanged) return; // disk truly unchanged

  if (tab.content.isDirty) {
    warnUnsavedConflict(filePath);
    return;
  }
  ws.updateTabContent(tab.id, {
    cells: freshContent.cells,
    metadata: freshContent.metadata,
    lastSavedHash: freshHash,
  });
}

/**
 * Deterministic JSON stringify with sorted object keys — a local sorted-key
 * serializer for comparing notebook metadata regardless of key order. Returns
 * `''` for `undefined` so a missing metadata object compares as a stable value.
 */
function stableStringify(value: unknown): string {
  return (
    JSON.stringify(value, (_key, val) => {
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        const record = val as Record<string, unknown>;
        return Object.fromEntries(
          Object.keys(record)
            .sort()
            .map((k) => [k, record[k]]),
        );
      }
      return val;
    }) ?? ''
  );
}
